/**
 * Helper utilities for managing localStorage for assessments and course progress
 */

import { nowISO } from "./index";

const ASSESSMENT_PREFIX = 'assessment-';
const PROGRESS_PREFIX = 'course-progress-';
// Holds the evaluated result of an assessment so a reload after evaluation can land
// the student back on the result page instead of restarting the quiz.
const ASSESSMENT_RESULT_PREFIX = 'assessment-result-';

/**
 * Assessment Storage Methods
 */

export const getAssessmentAnswers = (assessmentId) => {
  try {
    const key = `${ASSESSMENT_PREFIX}${assessmentId}`;
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Error reading assessment answers:', error);
    return [];
  }
};

export const saveAssessmentAnswers = (assessmentId, answers) => {
  try {
    const key = `${ASSESSMENT_PREFIX}${assessmentId}`;
    localStorage.setItem(key, JSON.stringify(answers));
  } catch (error) {
    console.error('Error saving assessment answers:', error);
  }
};

export const clearAssessmentAnswers = (assessmentId) => {
  try {
    const key = `${ASSESSMENT_PREFIX}${assessmentId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing assessment answers:', error);
  }
};

/**
 * Assessment Result Storage Methods
 *
 * Persist the evaluated result ({ evaluationResult, gradingResult }) keyed by
 * assessmentId so a reload after evaluation can rehydrate the result page.
 */

export const getAssessmentResult = (assessmentId) => {
  try {
    const key = `${ASSESSMENT_RESULT_PREFIX}${assessmentId}`;
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading assessment result:', error);
    return null;
  }
};

export const saveAssessmentResult = (assessmentId, resultState) => {
  try {
    const key = `${ASSESSMENT_RESULT_PREFIX}${assessmentId}`;
    localStorage.setItem(key, JSON.stringify(resultState));
  } catch (error) {
    console.error('Error saving assessment result:', error);
  }
};

export const clearAssessmentResult = (assessmentId) => {
  try {
    const key = `${ASSESSMENT_RESULT_PREFIX}${assessmentId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing assessment result:', error);
  }
};

export const clearAllAssessmentResults = () => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(ASSESSMENT_RESULT_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all assessment results:', error);
  }
};

/**
 * Course Progress Storage Methods
 */

export const getCourseProgress = (courseId) => {
  try {
    const key = `${PROGRESS_PREFIX}${courseId}`;
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading course progress:', error);
    return null;
  }
};

export const saveCourseProgress = (courseId, progress) => {
  try {
    const key = `${PROGRESS_PREFIX}${courseId}`;
    const data = {
      ...progress,
      lastUpdated: nowISO()
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving course progress:', error);
  }
};

export const clearCourseProgress = (courseId) => {
  try {
    const key = `${PROGRESS_PREFIX}${courseId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing course progress:', error);
  }
};

/**
 * Clear all assessment-related data from localStorage
 */
export const clearAllAssessmentData = () => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(ASSESSMENT_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all assessment data:', error);
  }
};

/**
 * Clear all course progress data from localStorage
 */
export const clearAllCourseProgress = () => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(PROGRESS_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all course progress:', error);
  }
};
